import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { faArchive, faChevronDown, faFilter } from "@fortawesome/pro-regular-svg-icons";

import { ReactComponent as Plus } from "@assets/icons/plus.svg";
import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import { Icon, PermissionCheck } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { InputField } from "@/components/InputField/InputField";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { PermissionRoles } from "@/enum";
import { ArchivedView } from "@/enum/list.enum";

import { PinFilterButton, PinSummaryItem } from "../../List/components/PinFilterButton";
import { ALL_SOURCES, SourceTypePalette } from "../../List/components/SourceTypePalette";
import { getSourceFilterEntry } from "../../List/config/sourceTypeFilterCatalog";
import { CampaignChannelFilter, CampaignStatusFilter } from "../utils/campaignFilters";

interface CampaignsFilterBarProps {
	searchValue: string;
	onSearchChange: (value: string) => void;
	statusFilter: CampaignStatusFilter;
	onStatusFilterChange: (value: CampaignStatusFilter) => void;
	channelFilter: CampaignChannelFilter;
	onChannelFilterChange: (value: CampaignChannelFilter) => void;
	archivedView: ArchivedView;
	onArchivedViewChange: (value: ArchivedView) => void;
	selectedSourceType?: string;
	onSourceTypeChange: (value: string) => void;
	onNewCampaign: () => void;
	/** Pin-to-tab: disabled while the current view has nothing worth saving. */
	pinDisabled: boolean;
	pinSuggestedName: string;
	pinSummaryItems: PinSummaryItem[];
	onPinCurrentView: (name: string) => void;
}

/**
 * Filter row of the Campaigns overview (PREC-3880): search, campaign status, channel, archived
 * lists, source type (⌘K palette), Pin to tab and the New Campaign button — mirroring the List
 * overview's row, down to the pin button sitting between the source palette and the primary
 * button. Presentational — every filter's state lives in `useCampaignsFilters`.
 */
export const CampaignsFilterBar: FunctionComponent<CampaignsFilterBarProps> = ({
	searchValue,
	onSearchChange,
	statusFilter,
	onStatusFilterChange,
	channelFilter,
	onChannelFilterChange,
	archivedView,
	onArchivedViewChange,
	selectedSourceType,
	onSourceTypeChange,
	onNewCampaign,
	pinDisabled,
	pinSuggestedName,
	pinSummaryItems,
	onPinCurrentView,
}) => {
	const { t } = useTranslation();
	const [showSourcePalette, setShowSourcePalette] = useState(false);

	// Open the source-type palette with ⌘K / Ctrl+K, mirroring the List overview.
	useEffect(() => {
		const handleHotkey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
				event.preventDefault();
				setShowSourcePalette(true);
			}
		};

		window.addEventListener("keydown", handleHotkey);

		return () => window.removeEventListener("keydown", handleHotkey);
	}, []);

	const statusOptions = Object.values(CampaignStatusFilter).map((value) => ({
		title: t(`campaignsOverview.statusFilter.${value}`),
		id: value,
	}));

	const channelOptions = Object.values(CampaignChannelFilter).map((value) => ({
		title: t(`campaignsOverview.channelFilter.${value}`),
		id: value,
	}));

	const sourceTypeText = (): string => {
		if (!selectedSourceType || selectedSourceType === ALL_SOURCES) {
			return t("list.sourceFilter.all");
		}

		const entry = getSourceFilterEntry(selectedSourceType);

		return entry ? t(entry.labelKey) : t("list.sourceFilter.all");
	};

	return (
		<div className="flex items-center gap-2 h-11 px-4 bg-white border-b border-border-light">
			<div className="w-[196px] flex-shrink-0">
				<InputField
					className="!rounded-[10px]"
					classNameWrapper="!mb-0"
					handleChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
					iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
					name="search"
					placeholder={t("campaignsOverview.searchPlaceholder")}
					size={ButtonSize.XS}
					value={searchValue}
				/>
			</div>
			<div className="ml-auto flex items-center gap-2">
				<div className="flex-shrink-0">
					<Dropdown
						classNameDropdown="w-max min-w-[180px]"
						data={statusOptions}
						defaultValue={statusOptions.find((option) => option.id === statusFilter)}
						floating={true}
						handleSelect={(value: AutoCompleteItem) =>
							onStatusFilterChange(value.id as CampaignStatusFilter)
						}
						icon={<Icon className="w-3.5 h-3.5" icon={faFilter} />}
						size={ButtonSize.XS}
						value={statusOptions.find((option) => option.id === statusFilter)}
					/>
				</div>
				<div className="flex-shrink-0">
					<Dropdown
						classNameDropdown="w-max min-w-[150px]"
						data={channelOptions}
						defaultValue={channelOptions.find((option) => option.id === channelFilter)}
						floating={true}
						handleSelect={(value: AutoCompleteItem) =>
							onChannelFilterChange(value.id as CampaignChannelFilter)
						}
						size={ButtonSize.XS}
						title={t("campaignsOverview.channelFilter.label")}
						value={channelOptions.find((option) => option.id === channelFilter)}
					/>
				</div>
				<div className="flex-shrink-0">
					<Dropdown
						data={[
							{ title: t("list.archivedView.HIDE"), id: ArchivedView.HIDE },
							{ title: t("list.archivedView.SHOW"), id: ArchivedView.SHOW },
						]}
						defaultValue={{ title: t(`list.archivedView.${archivedView}`), id: archivedView }}
						floating={true}
						icon={<Icon className="w-3.5 h-3.5" icon={faArchive} />}
						handleSelect={(value: AutoCompleteItem) =>
							onArchivedViewChange(value.id as ArchivedView)
						}
						size={ButtonSize.XS}
						value={{ title: t(`list.archivedView.${archivedView}`), id: archivedView }}
					/>
				</div>
				<div className="flex-shrink-0">
					<button
						className="flex h-[32px] cursor-pointer items-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-3 text-xs font-semibold text-text duration-200 hover:border-brand-light hover:shadow-sm"
						type="button"
						onClick={() => setShowSourcePalette(true)}
					>
						<span className="text-text-meta">{t("list.sourceFilter.trigger")}:</span>
						<span className="max-w-[140px] truncate">{sourceTypeText()}</span>
						<Icon className="h-3 w-3 text-text-meta" icon={faChevronDown} />
						<span className="ml-0.5 rounded border border-border-light px-1 py-px text-xxs font-medium text-text-disabled">
							⌘K
						</span>
					</button>
					<SourceTypePalette
						isOpen={showSourcePalette}
						selectedSourceType={selectedSourceType}
						onClose={() => setShowSourcePalette(false)}
						onSelect={onSourceTypeChange}
					/>
				</div>
				<PinFilterButton
					disabled={pinDisabled}
					suggestedName={pinSuggestedName}
					summaryItems={pinSummaryItems}
					onPin={onPinCurrentView}
				/>
				<PermissionCheck requiredPermissions={[PermissionRoles.LIST]}>
					<Button
						color={ButtonColor.ACTION}
						image={<SvgIcon className="w-[16px] h-[16px] mr-2" svgIcon={Plus} />}
						size={ButtonSize.S}
						title={t("campaignsOverview.newCampaign")}
						onClick={onNewCampaign}
					/>
				</PermissionCheck>
			</div>
		</div>
	);
};
